/**
 * Membership Stats parse boundary (WSRADM-0021).
 *
 * Total functions on `src/api/parse.ts` helpers — never throw. A malformed row
 * is dropped; good rows are kept. Envelope array is `content` (never `rows` /
 * `jobs`). Missing `durationColumns` → `[]`.
 */
import { asBoolean, asNumber, asText, isObject } from '@/api/parse';
import type {
  ComparisonBlock,
  DurationColumn,
  FamilyColumn,
  MemberState,
  MembershipBandCell,
  MembershipComparable,
  MembershipFamilyColumn,
  MembershipFamilyMetric,
  MembershipFamiliesMonthContent,
  MembershipFamiliesResponse,
  MembershipMember,
  MembershipMembersPage,
  MembershipMonthComparisons,
  MembershipMonthDetail,
  MembershipMonthRow,
  MembershipMonthsPage,
  ReportFreshnessFields,
  RevenueComparable,
  RevenueComparableFamily,
  RevenueFamilyCell,
  RevenueLineRow,
  RevenueLinesPage,
  RevenueMemberRow,
  RevenueMembersPage,
  RevenueDimensionOption,
  RevenueMonthComparisons,
  RevenueMonthDetail,
  RevenueMonthRow,
  RevenueMonthsPage,
  ActiveMembersSemantic,
  AttendanceBand,
  CheckinBandCell,
  RevenueOverlapProduct,
  RevenueProductRow,
  RevenueProductsPage,
  RevenueReconciliation,
  SeriesBucket,
  SeriesFamilyCell,
  SeriesGranularity,
  SeriesResponse,
} from './types';

const MEMBER_STATES: ReadonlySet<string> = new Set(['NEW', 'RETURNING', 'EXISTING', 'LOST', 'LAPSED']);

function parseFreshness(raw: Record<string, unknown>): ReportFreshnessFields {
  return {
    asOf: asText(raw['asOf']) ?? '',
    stale: asBoolean(raw['stale']) ?? false,
    neverComputed: asBoolean(raw['neverComputed']) ?? false,
    timezone: asText(raw['timezone']) ?? '',
  };
}

function emptyFreshness(): ReportFreshnessFields {
  return { asOf: '', stale: false, neverComputed: false, timezone: '' };
}

function emptyMonthRow(): MembershipMonthRow {
  return {
    month: '',
    lost: 0,
    lapsed: 0,
    existing: 0,
    new: 0,
    returning: 0,
    coveredMembers: 0,
    netNewMembers: 0,
    bands: [],
    comparisons: null,
  };
}

function parseDurationColumn(raw: unknown): DurationColumn | null {
  if (!isObject(raw)) return null;
  const bandKey = asText(raw['bandKey']);
  if (bandKey === null) return null;
  return {
    bandKey,
    bandDays: asNumber(raw['bandDays']),
    label: asText(raw['label']) ?? '',
  };
}

function parseDurationColumns(value: unknown): DurationColumn[] {
  if (!Array.isArray(value)) return [];
  const out: DurationColumn[] = [];
  for (const item of value) {
    const parsed = parseDurationColumn(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseBandCell(raw: unknown, allowed: ReadonlySet<string> | null): MembershipBandCell | null {
  if (!isObject(raw)) return null;
  const bandKey = asText(raw['bandKey']);
  if (bandKey === null) return null;
  if (allowed !== null && !allowed.has(bandKey)) return null;
  return {
    bandKey,
    new: asNumber(raw['new']) ?? 0,
    returning: asNumber(raw['returning']) ?? 0,
  };
}

function parseBands(value: unknown, allowed: ReadonlySet<string> | null): MembershipBandCell[] {
  if (!Array.isArray(value)) return [];
  const out: MembershipBandCell[] = [];
  for (const item of value) {
    const parsed = parseBandCell(item, allowed);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseComparisonBlock<T>(
  raw: unknown,
  parseValues: (v: Record<string, unknown>) => T,
): ComparisonBlock<T> | null {
  if (!isObject(raw)) return null;
  const available = asBoolean(raw['available']) ?? false;
  const month = asText(raw['month']) ?? undefined;
  const months = Array.isArray(raw['months']) ? parseStringList(raw['months']) : undefined;
  let values: T | null = null;
  if (available) {
    const rawValues = isObject(raw['values'])
      ? (raw['values'] as Record<string, unknown>)
      : isObject(raw['metrics'])
      ? (raw['metrics'] as Record<string, unknown>)
      : raw;
    values = parseValues(rawValues);
  }
  return { available, month, months, values };
}

function parseMembershipComparable(raw: Record<string, unknown>): MembershipComparable {
  return {
    lost: asNumber(raw['lost']),
    lapsed: asNumber(raw['lapsed']),
    coveredMembers: asNumber(raw['coveredMembers']),
    netNewMembers: asNumber(raw['netNewMembers']),
    existing: asNumber(raw['existing']),
    new: asNumber(raw['new']),
    returning: asNumber(raw['returning']),
  };
}

function parseMembershipComparisons(raw: unknown): MembershipMonthComparisons | null {
  if (!isObject(raw)) return null;
  const sameMonthLastYear = parseComparisonBlock(
    raw['sameMonthLastYear'] ?? raw['lastYear'],
    parseMembershipComparable,
  );
  const trailing3MonthAverage = parseComparisonBlock(
    raw['trailing3MonthAverage'] ?? raw['trailing3'],
    parseMembershipComparable,
  );
  if (!sameMonthLastYear && !trailing3MonthAverage) return null;
  return {
    sameMonthLastYear: sameMonthLastYear ?? { available: false, values: null },
    trailing3MonthAverage: trailing3MonthAverage ?? { available: false, values: null },
  };
}

function parseMonthRow(raw: unknown, allowed: ReadonlySet<string> | null): MembershipMonthRow | null {
  if (!isObject(raw)) return null;
  const month = asText(raw['month']);
  if (month === null) return null;
  return {
    month,
    lost: asNumber(raw['lost']) ?? 0,
    lapsed: asNumber(raw['lapsed']) ?? 0,
    existing: asNumber(raw['existing']) ?? 0,
    new: asNumber(raw['new']) ?? 0,
    returning: asNumber(raw['returning']) ?? 0,
    coveredMembers: asNumber(raw['coveredMembers']) ?? 0,
    netNewMembers: asNumber(raw['netNewMembers']) ?? 0,
    bands: parseBands(raw['bands'], allowed),
    comparisons: parseMembershipComparisons(raw['comparisons']),
  };
}

function emptyMonthsPage(): MembershipMonthsPage {
  return {
    ...emptyFreshness(),
    content: [],
    page: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
    durationColumns: [],
  };
}

export function parseMembershipMonthsPage(body: unknown): MembershipMonthsPage {
  if (!isObject(body)) return emptyMonthsPage();
  const durationColumns = parseDurationColumns(body['durationColumns']);
  const allowed = new Set(durationColumns.map((column) => column.bandKey));
  const content: MembershipMonthRow[] = [];
  if (Array.isArray(body['content'])) {
    for (const entry of body['content']) {
      const row = parseMonthRow(entry, allowed);
      if (row !== null) content.push(row);
    }
  }
  return {
    ...parseFreshness(body),
    content,
    page: asNumber(body['page']) ?? 0,
    size: asNumber(body['size']) ?? 0,
    totalElements: asNumber(body['totalElements']) ?? 0,
    totalPages: asNumber(body['totalPages']) ?? 0,
    durationColumns,
  };
}

export function parseMembershipMonthDetail(body: unknown): MembershipMonthDetail {
  if (!isObject(body)) {
    return { ...emptyMonthRow(), ...emptyFreshness() };
  }
  const row = parseMonthRow(body, null) ?? emptyMonthRow();
  return { ...row, ...parseFreshness(body) };
}

export function parseMembershipMember(raw: unknown): MembershipMember | null {
  if (!isObject(raw)) return null;
  const sourceUserId = asNumber(raw['sourceUserId']);
  const stateRaw = asText(raw['state']);
  if (sourceUserId === null || stateRaw === null || !MEMBER_STATES.has(stateRaw)) return null;
  return {
    sourceUserId,
    memberName: asText(raw['memberName']),
    state: stateRaw as MemberState,
    bandKey: asText(raw['bandKey']),
    bandLabel: asText(raw['bandLabel']),
    latestProductName: asText(raw['latestProductName']),
    latestPurchaseAt: asText(raw['latestPurchaseAt']),
    purchaseCount: asNumber(raw['purchaseCount']) ?? 0,
  };
}

export function parseMembershipMembersPage(body: unknown): MembershipMembersPage {
  if (!isObject(body)) {
    return {
      content: [],
      page: 0,
      size: 0,
      totalElements: 0,
      totalPages: 0,
      asOf: '',
      stale: false,
    };
  }
  const content: MembershipMember[] = [];
  if (Array.isArray(body['content'])) {
    for (const entry of body['content']) {
      const row = parseMembershipMember(entry);
      if (row !== null) content.push(row);
    }
  }
  return {
    content,
    page: asNumber(body['page']) ?? 0,
    size: asNumber(body['size']) ?? 0,
    totalElements: asNumber(body['totalElements']) ?? 0,
    totalPages: asNumber(body['totalPages']) ?? 0,
    asOf: asText(body['asOf']) ?? '',
    stale: asBoolean(body['stale']) ?? false,
  };
}

function parseStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    const text = asText(item);
    if (text !== null) out.push(text);
  }
  return out;
}

function parseFamilyColumn(raw: unknown): FamilyColumn | null {
  if (!isObject(raw)) return null;
  const displayName = asText(raw['displayName']);
  if (displayName === null) return null;
  const slugRaw = raw['slug'];
  let slug: string | null = null;
  if (slugRaw !== null && slugRaw !== undefined) {
    slug = asText(slugRaw);
    if (slug === null) return null;
  }
  return {
    slug,
    displayName,
    sortOrder: asNumber(raw['sortOrder']) ?? 0,
    color: asText(raw['color']),
  };
}

function parseDimensionOption(raw: unknown): RevenueDimensionOption | null {
  if (!isObject(raw)) return null;
  const slug = asText(raw['slug']);
  const displayName = asText(raw['displayName']);
  if (slug === null || displayName === null) return null;
  return {
    slug,
    displayName,
    readOnly: asBoolean(raw['readOnly']) ?? false,
  };
}

function parseDimensions(value: unknown): RevenueDimensionOption[] {
  if (!Array.isArray(value)) return [];
  const out: RevenueDimensionOption[] = [];
  for (const item of value) {
    const parsed = parseDimensionOption(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseRevenueDimensionFields(raw: Record<string, unknown>): {
  dimension: string | null;
  dimensions: RevenueDimensionOption[];
  sumsToTotal: boolean;
  transitional: boolean;
} {
  return {
    dimension: asText(raw['dimension']),
    dimensions: parseDimensions(raw['dimensions']),
    sumsToTotal: raw['sumsToTotal'] === true,
    transitional: raw['transitional'] === true,
  };
}

function parseFamilyColumns(value: unknown): FamilyColumn[] {
  if (!Array.isArray(value)) return [];
  const out: FamilyColumn[] = [];
  for (const item of value) {
    const parsed = parseFamilyColumn(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function familySlugKey(slug: string | null): string {
  return slug === null ? '' : slug;
}

function parseFamilyCell(
  raw: unknown,
  allowed: ReadonlySet<string> | null,
): RevenueFamilyCell | null {
  if (!isObject(raw)) return null;
  const slugRaw = raw['familySlug'];
  let familySlug: string | null = null;
  if (slugRaw !== null && slugRaw !== undefined) {
    familySlug = asText(slugRaw);
    if (familySlug === null) return null;
  }
  if (allowed !== null && !allowed.has(familySlugKey(familySlug))) return null;
  return {
    familySlug,
    revenue: asNumber(raw['revenue']) ?? 0,
    lineCount: asNumber(raw['lineCount']) ?? 0,
    unitCount: asNumber(raw['unitCount']) ?? 0,
    distinctMembers: asNumber(raw['distinctMembers']) ?? 0,
    average: asNumber(raw['average']),
    walkInLineCount: asNumber(raw['walkInLineCount']) ?? 0,
  };
}

function parseFamilyCells(value: unknown, allowed: ReadonlySet<string> | null): RevenueFamilyCell[] {
  if (!Array.isArray(value)) return [];
  const out: RevenueFamilyCell[] = [];
  for (const item of value) {
    const parsed = parseFamilyCell(item, allowed);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseOverlapProduct(raw: unknown): RevenueOverlapProduct | null {
  if (!isObject(raw)) return null;
  const sourceProductId = asNumber(raw['sourceProductId']);
  if (sourceProductId === null) return null;
  return {
    sourceProductId,
    productName: asText(raw['productName']),
    familySlugs: parseStringList(raw['familySlugs']),
    lineCount: asNumber(raw['lineCount']) ?? 0,
  };
}

function parseOverlapProducts(raw: unknown): RevenueOverlapProduct[] {
  if (!Array.isArray(raw)) return [];
  const out: RevenueOverlapProduct[] = [];
  for (const item of raw) {
    const parsed = parseOverlapProduct(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseRevenueComparableFamily(raw: unknown): RevenueComparableFamily | null {
  if (!isObject(raw)) return null;
  const slugRaw = raw['familySlug'];
  let familySlug: string | null = null;
  if (slugRaw !== null && slugRaw !== undefined) {
    familySlug = asText(slugRaw);
  }
  return {
    familySlug,
    revenue: asNumber(raw['revenue']),
    average: asNumber(raw['average']),
  };
}

function parseRevenueComparable(raw: Record<string, unknown>): RevenueComparable {
  const families: RevenueComparableFamily[] = [];
  if (Array.isArray(raw['families'])) {
    for (const f of raw['families']) {
      const parsed = parseRevenueComparableFamily(f);
      if (parsed !== null) families.push(parsed);
    }
  }
  return {
    totalRevenue: asNumber(raw['totalRevenue']),
    coveredMembers: asNumber(raw['coveredMembers']),
    arm: asNumber(raw['arm']),
    families,
  };
}

function parseRevenueComparisons(raw: unknown): RevenueMonthComparisons | null {
  if (!isObject(raw)) return null;
  const sameMonthLastYear = parseComparisonBlock(
    raw['sameMonthLastYear'] ?? raw['lastYear'],
    parseRevenueComparable,
  );
  const trailing3MonthAverage = parseComparisonBlock(
    raw['trailing3MonthAverage'] ?? raw['trailing3'],
    parseRevenueComparable,
  );
  if (!sameMonthLastYear && !trailing3MonthAverage) return null;
  return {
    sameMonthLastYear: sameMonthLastYear ?? { available: false, values: null },
    trailing3MonthAverage: trailing3MonthAverage ?? { available: false, values: null },
  };
}

function emptyRevenueMonthRow(): RevenueMonthRow {
  return {
    month: '',
    totalRevenue: 0,
    coveredMembers: 0,
    arm: null,
    families: [],
    overlapLineCount: 0,
    overlapProducts: [],
    overlapProductsTruncated: 0,
    comparisons: null,
  };
}

function parseRevenueMonthRow(
  raw: unknown,
  allowed: ReadonlySet<string> | null,
): RevenueMonthRow | null {
  if (!isObject(raw)) return null;
  const month = asText(raw['month']);
  if (month === null) return null;
  return {
    month,
    totalRevenue: asNumber(raw['totalRevenue']) ?? 0,
    coveredMembers: asNumber(raw['coveredMembers']) ?? 0,
    arm: asNumber(raw['arm']),
    families: parseFamilyCells(raw['families'], allowed),
    overlapLineCount: asNumber(raw['overlapLineCount']) ?? 0,
    overlapProducts: parseOverlapProducts(raw['overlapProducts']),
    overlapProductsTruncated: asNumber(raw['overlapProductsTruncated']) ?? 0,
    comparisons: parseRevenueComparisons(raw['comparisons']),
  };
}

function emptyRevenueMonthsPage(): RevenueMonthsPage {
  return {
    ...emptyFreshness(),
    content: [],
    page: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
    currency: '',
    currencies: [],
    familyColumns: [],
    dimension: null,
    dimensions: [],
    sumsToTotal: false,
    transitional: false,
  };
}

function parseReconciliation(raw: unknown): RevenueReconciliation {
  if (!isObject(raw)) {
    return { lineAmountTotal: 0, saleTotalSum: 0, delta: 0, saleCount: 0 };
  }
  return {
    lineAmountTotal: asNumber(raw['lineAmountTotal']) ?? 0,
    saleTotalSum: asNumber(raw['saleTotalSum']) ?? 0,
    delta: asNumber(raw['delta']) ?? 0,
    saleCount: asNumber(raw['saleCount']) ?? 0,
  };
}

export function parseRevenueMonthsPage(body: unknown): RevenueMonthsPage {
  if (!isObject(body)) return emptyRevenueMonthsPage();
  const familyColumns = parseFamilyColumns(body['familyColumns']);
  const allowed = new Set(familyColumns.map((column) => familySlugKey(column.slug)));
  const content: RevenueMonthRow[] = [];
  if (Array.isArray(body['content'])) {
    for (const entry of body['content']) {
      const row = parseRevenueMonthRow(entry, allowed);
      if (row !== null) content.push(row);
    }
  }
  return {
    ...parseFreshness(body),
    content,
    page: asNumber(body['page']) ?? 0,
    size: asNumber(body['size']) ?? 0,
    totalElements: asNumber(body['totalElements']) ?? 0,
    totalPages: asNumber(body['totalPages']) ?? 0,
    currency: asText(body['currency']) ?? '',
    currencies: parseStringList(body['currencies']),
    familyColumns,
    ...parseRevenueDimensionFields(body),
  };
}

export function parseRevenueMonthDetail(body: unknown): RevenueMonthDetail {
  if (!isObject(body)) {
    return {
      ...emptyRevenueMonthRow(),
      ...emptyFreshness(),
      currency: '',
      currencies: [],
      familyColumns: [],
      reconciliation: parseReconciliation(null),
      dimension: null,
      dimensions: [],
      sumsToTotal: false,
      transitional: false,
    };
  }
  const familyColumns = parseFamilyColumns(body['familyColumns']);
  const allowed = familyColumns.length > 0
    ? new Set(familyColumns.map((column) => familySlugKey(column.slug)))
    : null;
  const row = parseRevenueMonthRow(body, allowed) ?? emptyRevenueMonthRow();
  return {
    ...row,
    ...parseFreshness(body),
    currency: asText(body['currency']) ?? '',
    currencies: parseStringList(body['currencies']),
    familyColumns,
    reconciliation: parseReconciliation(body['reconciliation']),
    ...parseRevenueDimensionFields(body),
  };
}

export function parseRevenueProductRow(raw: unknown): RevenueProductRow | null {
  if (!isObject(raw)) return null;
  const productKey = asText(raw['productKey']);
  if (productKey === null) return null;
  return {
    productKey,
    productName: asText(raw['productName']),
    revenue: asNumber(raw['revenue']) ?? 0,
    lineCount: asNumber(raw['lineCount']) ?? 0,
    unitCount: asNumber(raw['unitCount']) ?? 0,
    distinctMembers: asNumber(raw['distinctMembers']) ?? 0,
    average: asNumber(raw['average']),
    inCatalogue: asBoolean(raw['inCatalogue']) ?? false,
  };
}

function emptyListPage<T>(): {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  asOf: string;
  stale: boolean;
} {
  return {
    content: [],
    page: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
    asOf: '',
    stale: false,
  };
}

export function parseRevenueProductsPage(body: unknown): RevenueProductsPage {
  if (!isObject(body)) return emptyListPage();
  const content: RevenueProductRow[] = [];
  if (Array.isArray(body['content'])) {
    for (const entry of body['content']) {
      const row = parseRevenueProductRow(entry);
      if (row !== null) content.push(row);
    }
  }
  return {
    content,
    page: asNumber(body['page']) ?? 0,
    size: asNumber(body['size']) ?? 0,
    totalElements: asNumber(body['totalElements']) ?? 0,
    totalPages: asNumber(body['totalPages']) ?? 0,
    asOf: asText(body['asOf']) ?? '',
    stale: asBoolean(body['stale']) ?? false,
  };
}

export function parseRevenueMemberRow(raw: unknown): RevenueMemberRow | null {
  if (!isObject(raw)) return null;
  return {
    sourceClientId: asNumber(raw['sourceClientId']),
    memberName: asText(raw['memberName']),
    walkIn: asBoolean(raw['walkIn']) ?? false,
    lineCount: asNumber(raw['lineCount']) ?? 0,
    unitCount: asNumber(raw['unitCount']) ?? 0,
    revenue: asNumber(raw['revenue']) ?? 0,
  };
}

export function parseRevenueMembersPage(body: unknown): RevenueMembersPage {
  if (!isObject(body)) return emptyListPage();
  const content: RevenueMemberRow[] = [];
  if (Array.isArray(body['content'])) {
    for (const entry of body['content']) {
      const row = parseRevenueMemberRow(entry);
      if (row !== null) content.push(row);
    }
  }
  return {
    content,
    page: asNumber(body['page']) ?? 0,
    size: asNumber(body['size']) ?? 0,
    totalElements: asNumber(body['totalElements']) ?? 0,
    totalPages: asNumber(body['totalPages']) ?? 0,
    asOf: asText(body['asOf']) ?? '',
    stale: asBoolean(body['stale']) ?? false,
  };
}

export function parseRevenueLineRow(raw: unknown): RevenueLineRow | null {
  if (!isObject(raw)) return null;
  const sourceSaleId = asNumber(raw['sourceSaleId']);
  const sourceDetailId = asNumber(raw['sourceDetailId']);
  if (sourceSaleId === null || sourceDetailId === null) return null;
  return {
    sourceSaleId,
    sourceDetailId,
    saleDate: asText(raw['saleDate']) ?? '',
    sourceClientId: asNumber(raw['sourceClientId']),
    memberName: asText(raw['memberName']),
    productName: asText(raw['productName']),
    quantity: asNumber(raw['quantity']) ?? 0,
    payedPrice: asNumber(raw['payedPrice']) ?? 0,
    lineAmount: asNumber(raw['lineAmount']) ?? 0,
    documentNumber: asNumber(raw['documentNumber']),
    paymentAccountName: asText(raw['paymentAccountName']),
  };
}

export function parseRevenueLinesPage(body: unknown): RevenueLinesPage {
  if (!isObject(body)) return emptyListPage();
  const content: RevenueLineRow[] = [];
  if (Array.isArray(body['content'])) {
    for (const entry of body['content']) {
      const row = parseRevenueLineRow(entry);
      if (row !== null) content.push(row);
    }
  }
  return {
    content,
    page: asNumber(body['page']) ?? 0,
    size: asNumber(body['size']) ?? 0,
    totalElements: asNumber(body['totalElements']) ?? 0,
    totalPages: asNumber(body['totalPages']) ?? 0,
    asOf: asText(body['asOf']) ?? '',
    stale: asBoolean(body['stale']) ?? false,
  };
}

function parseSeriesGranularity(raw: unknown): SeriesGranularity {
  return asText(raw) === 'day' ? 'day' : 'month';
}

function parseActiveMembersSemantic(raw: unknown): ActiveMembersSemantic {
  return asText(raw) === 'COVERED_ON_DAY' ? 'COVERED_ON_DAY' : 'COVERED_IN_MONTH';
}

function parseAttendanceBand(raw: unknown): AttendanceBand | null {
  if (!isObject(raw)) return null;
  const label = asText(raw['label']);
  if (label === null) return null;
  return {
    label,
    fromHour: asNumber(raw['fromHour']) ?? 0,
    toHour: asNumber(raw['toHour']) ?? 0,
  };
}

function parseAttendanceBands(value: unknown): AttendanceBand[] {
  if (!Array.isArray(value)) return [];
  const out: AttendanceBand[] = [];
  for (const item of value) {
    const parsed = parseAttendanceBand(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseCheckinBandCell(raw: unknown): CheckinBandCell | null {
  if (!isObject(raw)) return null;
  const label = asText(raw['label']);
  if (label === null) return null;
  return {
    label,
    count: asNumber(raw['count']) ?? 0,
    synthetic: asBoolean(raw['synthetic']) ?? false,
  };
}

function parseCheckinBands(value: unknown): CheckinBandCell[] {
  if (!Array.isArray(value)) return [];
  const out: CheckinBandCell[] = [];
  for (const item of value) {
    const parsed = parseCheckinBandCell(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseHourCount(raw: unknown): { hour: number; count: number } | null {
  if (!isObject(raw)) return null;
  const hour = asNumber(raw['hour']);
  if (hour === null) return null;
  return { hour, count: asNumber(raw['count']) ?? 0 };
}

function parseCheckinsByHour(value: unknown): { hour: number; count: number }[] {
  if (!Array.isArray(value)) return [];
  const out: { hour: number; count: number }[] = [];
  for (const item of value) {
    const parsed = parseHourCount(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseSeriesFamilyCell(raw: unknown): SeriesFamilyCell | null {
  if (!isObject(raw)) return null;
  const slugRaw = raw['slug'];
  let slug: string | null = null;
  if (slugRaw !== null && slugRaw !== undefined) {
    slug = asText(slugRaw);
    if (slug === null) return null;
  }
  return {
    slug,
    revenue: asNumber(raw['revenue']) ?? 0,
    lineCount: asNumber(raw['lineCount']) ?? 0,
  };
}

function parseSeriesFamilies(value: unknown): SeriesFamilyCell[] {
  if (!Array.isArray(value)) return [];
  const out: SeriesFamilyCell[] = [];
  for (const item of value) {
    const parsed = parseSeriesFamilyCell(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

function parseSeriesBucket(raw: unknown): SeriesBucket | null {
  if (!isObject(raw)) return null;
  const period = asText(raw['period']);
  if (period === null) return null;
  return {
    period,
    checkins: asNumber(raw['checkins']) ?? 0,
    activeMembers: asNumber(raw['activeMembers']) ?? 0,
    revenue: asNumber(raw['revenue']) ?? 0,
    lineCount: asNumber(raw['lineCount']) ?? 0,
    checkinsByHour: parseCheckinsByHour(raw['checkinsByHour']),
    checkinBands: parseCheckinBands(raw['checkinBands']),
    families: parseSeriesFamilies(raw['families']),
    overlapLineCount: asNumber(raw['overlapLineCount']) ?? 0,
    overlapProducts: parseOverlapProducts(raw['overlapProducts']),
    overlapProductsTruncated: asNumber(raw['overlapProductsTruncated']) ?? 0,
  };
}

function emptySeriesResponse(): SeriesResponse {
  return {
    asOf: null,
    stale: false,
    neverComputed: false,
    timezone: '',
    from: '',
    to: '',
    granularity: 'month',
    currency: '',
    currencies: [],
    dimension: null,
    dimensions: [],
    familyColumns: [],
    sumsToTotal: false,
    attendanceBands: [],
    attendanceBandsDefault: false,
    activeMembersSemantic: 'COVERED_IN_MONTH',
    buckets: [],
    checkinsByHourTotal: [],
  };
}

/** Lenient parser for `GET /admin/reports/series` (WSRADM-0037 / WSRADM-0052). */
export function parseSeriesResponse(body: unknown): SeriesResponse {
  if (!isObject(body)) return emptySeriesResponse();
  const buckets: SeriesBucket[] = [];
  if (Array.isArray(body['buckets'])) {
    for (const entry of body['buckets']) {
      const bucket = parseSeriesBucket(entry);
      if (bucket !== null) buckets.push(bucket);
    }
  }
  const asOfRaw = body['asOf'];
  const asOf = asOfRaw === null || asOfRaw === undefined ? null : (asText(asOfRaw) ?? null);
  return {
    asOf,
    stale: asBoolean(body['stale']) ?? false,
    neverComputed: asBoolean(body['neverComputed']) ?? false,
    timezone: asText(body['timezone']) ?? '',
    from: asText(body['from']) ?? '',
    to: asText(body['to']) ?? '',
    granularity: parseSeriesGranularity(body['granularity']),
    currency: asText(body['currency']) ?? '',
    currencies: parseStringList(body['currencies']),
    dimension: parseDimensionOption(body['dimension']),
    dimensions: parseDimensions(body['dimensions']),
    familyColumns: parseFamilyColumns(body['familyColumns']),
    sumsToTotal: body['sumsToTotal'] === true,
    attendanceBands: parseAttendanceBands(body['attendanceBands']),
    attendanceBandsDefault: body['attendanceBandsDefault'] === true,
    activeMembersSemantic: parseActiveMembersSemantic(body['activeMembersSemantic']),
    buckets,
    checkinsByHourTotal: parseCheckinsByHour(body['checkinsByHourTotal']),
  };
}

function parseMembershipFamilyMetric(raw: unknown): MembershipFamilyMetric | null {
  if (!isObject(raw)) return null;
  const familySlug = asText(raw['familySlug']);
  if (familySlug === null) return null;
  return {
    familySlug,
    coveredMembers: asNumber(raw['coveredMembers']),
    lapsed: asNumber(raw['lapsed']),
    lost: asNumber(raw['lost']),
    new: asNumber(raw['new']),
    returning: asNumber(raw['returning']),
    movedIn: asNumber(raw['movedIn']),
    movedOut: asNumber(raw['movedOut']),
    momChange: asNumber(raw['momChange']),
    yoyChange: asNumber(raw['yoyChange']),
    churnRate: asNumber(raw['churnRate']),
  };
}

function parseMembershipFamiliesMonthContent(raw: unknown): MembershipFamiliesMonthContent | null {
  if (!isObject(raw)) return null;
  const month = asText(raw['month']);
  if (month === null) return null;
  const families: MembershipFamilyMetric[] = [];
  if (Array.isArray(raw['families'])) {
    for (const f of raw['families']) {
      const parsed = parseMembershipFamilyMetric(f);
      if (parsed !== null) families.push(parsed);
    }
  }
  return {
    month,
    overlapMembers: asNumber(raw['overlapMembers']) ?? 0,
    families,
  };
}

function parseMembershipFamilyColumn(raw: unknown): MembershipFamilyColumn | null {
  if (!isObject(raw)) return null;
  const slug = asText(raw['slug']);
  const displayName = asText(raw['displayName']);
  if (slug === null || displayName === null) return null;
  return {
    slug,
    displayName,
    color: asText(raw['color']),
  };
}

function emptyMembershipFamiliesResponse(): MembershipFamiliesResponse {
  return {
    ...emptyFreshness(),
    dimension: null,
    familyColumns: [],
    content: [],
  };
}

/** Lenient parser for `GET /admin/reports/membership/families` (WSRADM-0052). */
export function parseMembershipFamiliesResponse(body: unknown): MembershipFamiliesResponse {
  if (!isObject(body)) return emptyMembershipFamiliesResponse();
  const dimensionRaw = body['dimension'];
  let dimension: { slug: string; displayName: string } | null = null;
  if (isObject(dimensionRaw)) {
    const slug = asText(dimensionRaw['slug']);
    const displayName = asText(dimensionRaw['displayName']);
    if (slug !== null && displayName !== null) {
      dimension = { slug, displayName };
    }
  }

  const familyColumns: MembershipFamilyColumn[] = [];
  if (Array.isArray(body['familyColumns'])) {
    for (const c of body['familyColumns']) {
      const parsed = parseMembershipFamilyColumn(c);
      if (parsed !== null) familyColumns.push(parsed);
    }
  }

  const content: MembershipFamiliesMonthContent[] = [];
  if (Array.isArray(body['content'])) {
    for (const item of body['content']) {
      const parsed = parseMembershipFamiliesMonthContent(item);
      if (parsed !== null) content.push(parsed);
    }
  }

  return {
    ...parseFreshness(body),
    dimension,
    familyColumns,
    content,
  };
}
